/**
 * Base HTTP client.
 * Wraps fetch with JSON handling, credential forwarding (cookies),
 * and structured error parsing. All API calls go through this module.
 */

import type { ApiError } from "./types.js";

// Allow environment variable override for API base URL
// In production (Cloudflare Pages), set VITE_API_BASE to your Render API URL
// In development with vite dev server, relative URLs work via proxy config
export const API_BASE = typeof import.meta.env.VITE_API_BASE === "string" && import.meta.env.VITE_API_BASE ? import.meta.env.VITE_API_BASE : "";

/**
 * Fetch a CSRF token from the server.
 * Tokens are consumed on use, so we fetch fresh for each request.
 */
async function getCsrfToken(): Promise<string> {
  try {
    const res = await fetch(`${API_BASE}/csrf-token`, {
      method: "GET",
      credentials: "include",
    });

    if (res.ok) {
      const data = (await res.json()) as { token: string };
      return data.token;
    }

    // Token fetch failed - log locally only (don't try to report to backend)
    const errorMsg = `CSRF token fetch failed: ${res.status} ${res.statusText}`;
    if (res.status === 401) {
      console.warn(errorMsg + " (user not authenticated)");
    } else {
      console.error(errorMsg);
    }
  } catch (err) {
    // Network/fetch error - log locally only
    const errorMsg = err instanceof Error ? err.message : String(err);
    console.error(`CSRF token fetch error: ${errorMsg}`);
  }

  // Return empty string as fallback (server will reject with 403 if CSRF required)
  return "";
}

/**
 * Log an error asynchronously without blocking the application.
 * Uses fire-and-forget pattern with re-entrancy guard to prevent
 * infinite recursion when the error-logging endpoint itself fails.
 */
let _isLogging = false;
async function logErrorAsync(
  errorCode: string,
  message: string,
  context?: Record<string, unknown>,
): Promise<void> {
  if (_isLogging) return; // Prevent recursive logging loop
  _isLogging = true;
  try {
    // Dynamic import to avoid circular dependencies
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const admin = await import("./admin-errors.js").catch(() => null);
    if (admin?.logFrontendError) {
      await admin.logFrontendError(errorCode, message, context);
    }
  } catch {
    // Silently fail - never break the app due to error logging
  } finally {
    _isLogging = false;
  }
}

export class ApiCallError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = "ApiCallError";
    this.status = status;
    this.code = code;
  }
}

async function parseErrorBody(res: Response): Promise<ApiError> {
  try {
    const json = (await res.json()) as Partial<ApiError>;
    return {
      code: json.code ?? "UNKNOWN_ERROR",
      message: json.message ?? res.statusText,
    };
  } catch {
    return { code: "UNKNOWN_ERROR", message: res.statusText };
  }
}

export async function apiFetch<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const method = (options?.method || "GET").toUpperCase();

  // Fetch CSRF token for state-changing requests
  let headers = { "Content-Type": "application/json", ...(options?.headers ?? {}) };
  if (["POST", "PATCH", "DELETE"].includes(method)) {
    const csrfToken = await getCsrfToken();
    if (csrfToken) {
      headers = { ...headers, "X-CSRF-Token": csrfToken };
    }
  }

  try {
    const res = await fetch(url, {
      ...options,
      method,
      credentials: "include",
      headers,
    });

    if (!res.ok) {
      const err = await parseErrorBody(res);

      // 401 = session expired — fire event so global handler can redirect
      if (res.status === 401) {
        window.dispatchEvent(new CustomEvent("session-expired"));
        throw new ApiCallError(err.message, res.status, err.code);
      }

      // 403 CSRF failure — retry once with a fresh token
      if (
        res.status === 403 &&
        ["POST", "PATCH", "DELETE"].includes(method) &&
        (err.code === "FORBIDDEN" || err.message.toLowerCase().includes("csrf"))
      ) {
        const freshToken = await getCsrfToken();
        if (freshToken) {
          const retryHeaders = { ...headers, "X-CSRF-Token": freshToken };
          const retryRes = await fetch(url, {
            ...options,
            method,
            credentials: "include",
            headers: retryHeaders,
          });
          if (retryRes.ok) {
            if (retryRes.status === 204 || retryRes.headers.get("content-length") === "0") {
              return undefined as unknown as T;
            }
            return retryRes.json() as Promise<T>;
          }
          // Retry also failed — fall through to error handling
          const retryErr = await parseErrorBody(retryRes);
          throw new ApiCallError(
            "Something went wrong with your request. Please try again.",
            retryRes.status,
            retryErr.code,
          );
        }
      }

      // Log error asynchronously (fire-and-forget) with full context.
      // Skip 404 (expected "no data" for many endpoints) and 401 (session expired).
      if (res.status !== 404 && res.status !== 401) {
        void logErrorAsync(err.code, err.message, {
          surface: "api",
          endpoint: path,
          page: window.location.pathname,
          method,
          status: res.status,
          is_blocking: true,
        });
      }

      throw new ApiCallError(err.message, res.status, err.code);
    }

    if (res.status === 204 || res.headers.get("content-length") === "0") {
      return undefined as unknown as T;
    }

    return res.json() as Promise<T>;
  } catch (err) {
    // Log network/fetch errors
    if (err instanceof ApiCallError) {
      throw err;
    }

    void logErrorAsync("API_CALL_FAILED", String(err), {
      surface: "api",
      endpoint: path,
      page: window.location.pathname,
      method,
      errorType: err instanceof Error ? err.name : "Unknown",
      is_blocking: true,
    });

    throw err;
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: "GET" });
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function apiDelete<T = void>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: "DELETE" });
}
